/**
 * Field HUD — almost nothing over the camera.
 *
 * Everything that used to sit in a bottom bar now lives behind a single sigil in
 * the top-left corner. The camera view is the artwork; a permanent strip of
 * chips across it was the largest thing standing between the participant and
 * believing the room had been colonised.
 *
 * ⚠️ Three things deliberately stay OUT of the drawer:
 *
 *  1. The Watcher's eye (`visual/Sun/WatcherEye.tsx`) — it is the honest
 *     "frames are leaving your device" disclosure, and consent signalling that
 *     can be hidden is not signalling. It renders in App, above this.
 *  2. Ending the session — a participant must always be able to leave.
 *  3. Tracking-lost feedback, which is only useful in the moment it happens.
 *
 * The regime label stays visible too, but as a bare word rather than a control:
 * it is the work telling you what it is doing, which is the "show its own seams"
 * beat, not a setting.
 */

import { useEffect, useState } from 'react';
import type { RegimeName } from '@wintermute/orchestrator';
import { getRegime } from '@wintermute/orchestrator';
import { useStore } from '../state/store.js';
import { runtime } from '../session/runtime.js';

interface Props {
  onReflect: () => void;
  onEnd: () => void;
}

const REGIME_CYCLE: RegimeName[] = [
  'neutral',
  'crisis-swarm',
  'echo-well',
  'memory-vault',
  'comfort',
  'surprise',
];

export function FieldHud({ onReflect, onEnd }: Props) {
  const regime = useStore((s) => s.regime);
  const dominant = useStore((s) => s.dominant);
  const seams = useStore((s) => s.seams);
  const toggleSeams = useStore((s) => s.toggleSeams);
  const showScan = useStore((s) => s.showScan);
  const toggleScan = useStore((s) => s.toggleScan);
  const ground = useStore((s) => s.ground);
  const toggleGround = useStore((s) => s.toggleGround);
  const showSlam = useStore((s) => s.showSlamHud);
  const toggleSlam = useStore((s) => s.toggleSlamHud);
  const lastMutation = useStore((s) => s.lastMutation);
  const cameraError = useStore((s) => s.cameraError);
  const watcherConsent = useStore((s) => s.watcherConsent);
  const watcherVia = useStore((s) => s.lastObservation?.via ?? null);
  const watcherReason = useStore((s) => s.lastObservation?.reason);
  const watcherRegister = useStore((s) => s.lastObservation?.register);
  // Polled rather than pushed: anchor counts change on the tracker's clock, not
  // on a React state change, and only while the drawer is actually open.
  const [anchors, setAnchors] = useState<{ total: number; committed: number } | null>(null);
  const open = useStore((s) => s.drawerOpen);
  const toggleDrawer = useStore((s) => s.toggleDrawer);
  const setDrawerOpen = useStore((s) => s.setDrawerOpen);

  const cycleRegime = () => {
    const i = REGIME_CYCLE.indexOf(regime);
    runtime.setRegime(REGIME_CYCLE[(i + 1) % REGIME_CYCLE.length]!);
  };

  // Only while the drawer is open — a 1 Hz timer behind a closed panel is pure
  // waste on a device already running SLAM.
  useEffect(() => {
    if (!open) return;
    const read = () => setAnchors(runtime.spatial.anchorCounts?.() ?? null);
    read();
    const t = window.setInterval(read, 1000);
    return () => window.clearInterval(t);
  }, [open]);

  const close = () => setDrawerOpen(false);

  return (
    <>
      {/* The only permanent mark on the camera view. */}
      <button
        className={open ? 'drawer-sigil open' : 'drawer-sigil'}
        onClick={toggleDrawer}
        aria-label="controls"
        aria-expanded={open}
      >
        <svg viewBox="0 0 32 32" aria-hidden="true">
          <circle cx="16" cy="16" r="12" />
          <circle cx="16" cy="16" r="5" />
          <path d="M16 1 L16 31 M1 16 L31 16" />
        </svg>
      </button>

      {/* The work naming its own state. Not a control. */}
      <div className="hud-state">
        <span className="hud-regime-label">{getRegime(regime).label}</span>
        {dominant.length > 0 && <span className="hud-dominant">drawn to {dominant[0]}</span>}
      </div>

      {seams && lastMutation && (
        <div className="seams-ticker">
          it {lastMutation.op}d something: “{lastMutation.text}”
        </div>
      )}

      {cameraError && (
        <div className="camera-warn">
          no camera ({cameraError}) — it still feels how you move
        </div>
      )}

      {open && (
        <>
          <div className="drawer-scrim" onClick={close} />
          <div className="drawer" role="dialog" aria-label="controls">
            <button className="drawer-row" onClick={cycleRegime}>
              <span className="drawer-key">regime</span>
              <span className="drawer-val">{getRegime(regime).label}</span>
            </button>
            <button className="drawer-row" onClick={toggleSeams}>
              <span className="drawer-key">seams</span>
              <span className="drawer-val">{seams ? 'shown' : 'hidden'}</span>
            </button>
            <button className="drawer-row" onClick={toggleGround}>
              <span className="drawer-key">ground</span>
              <span className="drawer-val">{ground ? 'aged' : 'raw'}</span>
            </button>
            <button className="drawer-row" onClick={toggleScan}>
              <span className="drawer-key">scan</span>
              <span className="drawer-val">{showScan ? 'shown' : 'hidden'}</span>
            </button>
            <button className="drawer-row" onClick={toggleSlam}>
              <span className="drawer-key">tracking</span>
              <span className="drawer-val">{showSlam ? 'shown' : 'hidden'}</span>
            </button>

            {/* Watcher diagnostics. The stub is a legitimate fallback but it
                used to be indistinguishable from the model actually running,
                which is how four hardcoded lines passed for months as
                "atmospheric". `via` and the failure reason are now visible. */}
            {watcherConsent && (
              <div className="drawer-row static">
                <span className="drawer-key">watcher</span>
                <span className={watcherVia === 'stub' ? 'drawer-val warn' : 'drawer-val'}>
                  {watcherVia === null
                    ? 'not yet looked'
                    : watcherVia === 'llm'
                      ? 'model responding'
                      : `stub — ${watcherReason ?? 'unknown'}`}
                </span>
              </div>
            )}
            {/* The Watcher's own self-described register (server/src/observe.ts
                REGISTER_VOCABULARY) — descriptive only, not a stage anything
                advances through. Confirms the model is finding its own posture
                from memory rather than reciting a fixed script. */}
            {watcherConsent && watcherVia === 'llm' && watcherRegister && (
              <div className="drawer-row static">
                <span className="drawer-key">register</span>
                <span className="drawer-val">{watcherRegister}</span>
              </div>
            )}

            {/* How much text is actually pinned to the room. "Barely any text
                anchored" was previously impossible to diagnose from outside:
                an anchor never placed, one placed but hidden behind a
                confidence gate, and one placed correctly but projected
                off-screen all looked the same. */}
            {anchors && (
              <div className="drawer-row static">
                <span className="drawer-key">anchors</span>
                <span className={anchors.total === 0 ? 'drawer-val warn' : 'drawer-val'}>
                  {anchors.total === 0
                    ? 'none placed'
                    : `${anchors.committed}/${anchors.total} settled`}
                </span>
              </div>
            )}

            <div className="drawer-sep" />

            <button
              className="drawer-row action"
              onClick={() => {
                close();
                onReflect();
              }}
            >
              <span className="drawer-key">let it look at you</span>
            </button>
            <button
              className="drawer-row action end"
              onClick={() => {
                close();
                onEnd();
              }}
            >
              <span className="drawer-key">see your drift</span>
            </button>
          </div>
        </>
      )}
    </>
  );
}
